<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4">
          <v-toolbar elevation="2" color="surface">
            <span class="text-button text-indigo pl-4">{{ view.options.title }}</span>
            <v-spacer />

            <!-- escritorio -->
            <div class="d-none d-md-flex align-center">
              <slot name="actions" />

              <v-btn v-if="createTo" variant="text" color="indigo" :to="createTo">
                <v-icon class="mr-2">{{ createIcon }}</v-icon>{{ createLabel }}
              </v-btn>

              <v-tooltip location="bottom" text="Refrescar">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-refresh"
                    variant="text"
                    color="indigo"
                    @click="view.loadData()"
                  />
                </template>
              </v-tooltip>

              <v-menu v-if="hasFilters" location="bottom end">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-filter" variant="text" color="indigo" />
                </template>
                <v-list density="compact">
                  <v-list-subheader>FILTROS</v-list-subheader>
                  <v-list-item
                    v-for="option in view.options.filters"
                    :key="option.field"
                    @click="view.openFilterDialog(option)"
                  >
                    <v-list-item-title>{{ option.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <!-- móvil -->
            <div class="d-md-none">
              <v-btn icon="mdi-refresh" variant="text" color="indigo" @click="view.loadData()" />
              <v-menu location="bottom end">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" color="indigo" />
                </template>
                <v-list density="compact">
                  <v-list-item v-if="createTo" :to="createTo">
                    <v-list-item-title class="text-no-wrap">{{ createLabel }}</v-list-item-title>
                    <template #append><v-icon>{{ createIcon }}</v-icon></template>
                  </v-list-item>
                  <v-list-item
                    v-for="option in view.options.filters"
                    :key="option.field"
                    @click="view.openFilterDialog(option)"
                  >
                    <v-list-item-title class="text-no-wrap">
                      Filtrar por {{ option.label }}
                    </v-list-item-title>
                    <template #append><v-icon>mdi-filter</v-icon></template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-toolbar>

          <div v-if="view.activeFilters.value.length" class="px-4 py-2 d-flex flex-wrap ga-2">
            <v-chip
              v-for="f in view.activeFilters.value"
              :key="f.field"
              closable
              size="small"
              @click:close="view.removeFilter(f.field)"
            >
              {{ f.label }}: {{ f.value }}
            </v-chip>
          </div>

          <ag-data-table
            :options="columns"
            :items="view.data.value"
            :pagination="view.pagination.value"
            :total-elements="view.totalElements.value"
            :loading="view.loading.value"
            :row-key="view.options.rowKey"
            @pagination="view.onPaginationChange"
          />
        </v-card>

        <!-- filtro -->
        <v-dialog v-model="view.filterOpen.value" max-width="600" persistent>
          <v-card>
            <v-toolbar color="indigo" flat>
              <v-toolbar-title class="text-white">Filtros</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <div class="mb-4">Filtrar por {{ view.filterOption.value?.label }}</div>
              <v-form @submit.prevent="view.applyFilter()">
                <v-text-field
                  v-model="view.filterValue.value"
                  variant="solo"
                  autofocus
                  :placeholder="view.filterOption.value?.label"
                />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="view.closeFilterDialog()">Cancelar</v-btn>
              <v-btn
                variant="text"
                :disabled="!view.filterValue.value"
                @click="view.applyFilter()"
              >
                Aplicar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- borrado con confirmación escrita -->
        <v-dialog
          v-if="view.options.confirm"
          :model-value="!!view.selected.value"
          max-width="500"
          persistent
        >
          <v-card>
            <v-toolbar color="error" flat>
              <v-toolbar-title class="text-white">{{ removeTitle }}</v-toolbar-title>
            </v-toolbar>
            <v-card-text class="text-body-2">
              <v-alert variant="outlined" color="error" class="text-center">
                {{ view.selected.value ? view.options.confirm.warning(view.selected.value) : "" }}
              </v-alert>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="mb-1 mt-5" v-html="view.options.confirm.hint" />
              <v-form @submit.prevent="view.confirmRemove()">
                <v-text-field
                  v-model="view.confirmText.value"
                  variant="solo"
                  autofocus
                  :label="view.options.confirm.label"
                  :disabled="view.removing.value"
                  :error-messages="view.confirmError.value"
                />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn :disabled="view.removing.value" variant="text" @click="view.closeRemove()">
                Cancelar
              </v-btn>
              <v-btn
                :loading="view.removing.value"
                :disabled="!view.confirmMatches.value"
                variant="text"
                color="error"
                @click="view.confirmRemove()"
              >
                Eliminar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <slot name="dialogs" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
/**
 * Cascarón de una vista de índice: barra, filtros activos, tabla y diálogos.
 *
 * Recibe el objeto que devuelve useIndexView y lo usa tal cual, sin
 * desestructurar, para no perder la reactividad. Es un componente interno de la
 * app, así que acoplarlo a la forma del composable es aceptable y evita
 * reenviar quince props.
 */
import { computed } from "vue";
import AgDataTable from "@/components/table/AgDataTable.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import type { IndexView } from "@/composables/useIndexView";

const props = withDefaults(
  defineProps<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    view: IndexView<any, any>;
    columns: AgDataTableOptions[];
    /** Ruta ABSOLUTA del botón de alta. Sin ella no se muestra. */
    createTo?: string;
    createLabel?: string;
    createIcon?: string;
    removeTitle?: string;
  }>(),
  {
    createTo: undefined,
    createLabel: "Crear",
    createIcon: "mdi-plus",
    removeTitle: "Eliminar registro",
  }
);

const hasFilters = computed(() => (props.view.options.filters?.length ?? 0) > 0);
</script>
